import React, { useState } from 'react';
import { QuoteState } from '../types';
import { X, Check, Save, Download, Loader2, CheckCircle2, ShieldCheck, Database, FileText } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

interface QuoteFinalizeModalProps {
  isOpen: boolean;
  onClose: () => void;
  quote: QuoteState;
  onSave: (alias: string) => void;
  onDownload: () => void;
  onSendForApproval: () => Promise<void>;
}

export const QuoteFinalizeModal: React.FC<QuoteFinalizeModalProps> = ({ 
  isOpen, 
  onClose, 
  quote, 
  onSave 
}) => {
  const [alias, setAlias] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  if (!isOpen) return null;

  const handleDownloadPDF = async () => {
    const element = document.querySelector('.print-area') as HTMLElement;
    if (!element) return;

    setIsExporting(true);
    try {
      // 1. Prepare element for capture
      const originalShadow = element.style.boxShadow;
      const originalMargin = element.style.margin;
      const originalTransform = element.style.transform;
      
      element.style.boxShadow = 'none';
      element.style.margin = '0';
      element.style.transform = 'none';
      
      // 2. Capture using html2canvas with optimal settings for images and high resolution
      const canvas = await html2canvas(element, {
        scale: 3, 
        useCORS: true, 
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: element.offsetWidth,
        height: element.offsetHeight,
        imageTimeout: 15000, // Give plenty of time for remote logos to load
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector('.print-area') as HTMLElement;
          if (clonedElement) {
            clonedElement.style.transform = 'none';
            clonedElement.style.boxShadow = 'none';
            clonedElement.style.margin = '0';
          }
        }
      });

      // 3. Restore original UI state
      element.style.boxShadow = originalShadow;
      element.style.margin = originalMargin;
      element.style.transform = originalTransform;

      // 4. Create PDF with exact A4 geometry
      const imgData = canvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Add image to cover the entire page
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      
      // 5. Save with dynamic filename
      const safeNumber = quote.details.number.replace(/[/\\?%*:|"<>]/g, '_');
      pdf.save(`Proforma_${safeNumber}.pdf`);
      
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (err) {
      console.error('PDF Export Error:', err);
      alert('PDF Capture Fault: System could not finalize the A4 frame. Please check layout constraints or network connection for the logo.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSaveToSystem = async () => {
    setIsSaving(true);
    try {
        const finalAlias = alias.trim() || `Proforma ${quote.details.number}`;
        await onSave(finalAlias);
    } finally {
        setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-300 flex flex-col">
        <header className="p-8 bg-slate-900 text-white flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
              <ShieldCheck size={24} />
            </div>
            <div>
              <h3 className="text-xl font-black uppercase tracking-tight">Finalize Document</h3>
              <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">Registry Ref: {quote.details.number}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
            <X size={24} />
          </button>
        </header>

        <div className="p-8 space-y-8">
          <div className="space-y-2">
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3 ml-2">Internal Reference / Project Name</label>
            <input 
              type="text" 
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="e.g. Q1 Site Supply - Morogoro Node"
              className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-bold outline-none focus:ring-4 focus:ring-indigo-500/10 transition-all shadow-inner"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <button 
               onClick={handleDownloadPDF}
               disabled={isExporting}
               className="flex flex-col items-center gap-4 p-8 rounded-[32px] border-2 border-slate-100 hover:border-indigo-600 hover:bg-indigo-50/50 transition-all group"
             >
                <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  {isExporting ? <Loader2 className="animate-spin" size={28} /> : <Download size={28} />}
                </div>
                <div className="text-center">
                   <p className="font-black text-xs uppercase tracking-widest text-slate-800">Generate PDF</p>
                   <p className="text-[10px] text-slate-400 mt-1">High-Res A4 Format</p>
                </div>
             </button>

             <button 
               onClick={handleSaveToSystem}
               disabled={isSaving}
               className="flex flex-col items-center gap-4 p-8 rounded-[32px] border-2 border-slate-100 hover:border-emerald-600 hover:bg-emerald-50/50 transition-all group"
             >
                <div className="w-14 h-14 bg-emerald-50 text-emerald-600 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  {isSaving ? <Loader2 className="animate-spin" size={28} /> : <Database size={28} />}
                </div>
                <div className="text-center">
                   <p className="font-black text-xs uppercase tracking-widest text-slate-800">Archive to Ledger</p>
                   <p className="text-[10px] text-slate-400 mt-1">Permanent System Save</p>
                </div>
             </button>
          </div>

          {showSuccess && (
            <div className="p-4 bg-emerald-50 border border-emerald-100 text-emerald-700 rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-2">
              <CheckCircle2 size={18} />
              <p className="text-xs font-bold uppercase tracking-widest">Document exported successfully</p>
            </div>
          )}
          
          <div className="pt-4 text-center">
             <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest leading-relaxed">
                Note: The PDF is formatted for A4 standard (210mm x 297mm). Content is automatically scaled to fit perfectly within standard page dimensions.
             </p>
          </div>
        </div>

        <footer className="p-6 bg-slate-50 border-t border-slate-100 flex justify-center shrink-0">
          <p className="text-[9px] font-black text-slate-300 uppercase tracking-[0.4em]">Secure Audit Node Active</p>
        </footer>
      </div>
    </div>
  );
};
